import { useEffect, useState } from 'react';
import { useRows } from '@/hooks/useLive';
import { useSiteFilter } from '@/hooks/useSiteFilter';
import { save, softDelete, upsert } from '@/lib/crud';
import { dateTime, hours, num } from '@/lib/format';
import { db } from '@/lib/db';
import { useAuthStore } from '@/stores/authStore';
import { METER_UNITS, meterName, meterSuffix, unitOf, type MeterUnit } from '@/lib/meter';
import { MeterChangePanel, MeterReadings, MeterReplacePanel } from './MeterTools';
import { TransferHistory, TransferPanel } from './SiteTools';
import { DOCS, QUARRY_SITE_ID, docState } from '@/lib/fleet';
import { FUEL_TYPES } from '@/lib/fuel';
import { LockNotice } from '@/components/ui/LockNotice';
import { Drawer } from '@/components/ui/Drawer';
import { MachinePerformance } from './MachinePerformance';
import { Button } from '@/components/ui/Button';
import { ConfirmRemove } from '@/components/ui/ConfirmRemove';
import { StatusChip } from '@/components/ui/StatusChip';
import {
  CheckboxInput, NumberInput, SelectInput, TextArea, TextInput,
} from '@/components/ui/Field';
import { serviceState } from './serviceState';
import type { Machine, MachineType, Site } from '@/types/models';

type Draft = Partial<Machine> & Record<string, unknown>;

interface Props {
  machine: Machine | Partial<Machine>;
  types: MachineType[];
  amberHours: number;
  amberKm?: number;
  onClose: () => void;
  onSave: (values: Draft) => Promise<void>;
  onDelete: (id: string) => Promise<boolean>;
  /** Why this person may look but not change (role), if so. */
  locked?: string | null;
}

const STATUSES = [
  { value: 'active', label: 'Active — working or available' },
  { value: 'standby', label: 'Standby' },
  { value: 'down', label: 'Down (set by breakdowns)' },
  { value: 'maintenance', label: 'In maintenance' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'disposed', label: 'Disposed' },
];

export function MachineDrawer({ machine, types, amberHours, amberKm, onClose, onSave, onDelete, locked }: Props) {
  const isNew = !machine.id;
  const [v, setV] = useState<Draft>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState<'details' | 'performance' | 'readings'>('details');
  const [pendingUnit, setPendingUnit] = useState<MeterUnit | null>(null);
  const canCorrect = useAuthStore((s) => s.hasRole('manager', 'admin'));
  const canMove = useAuthStore((s) => s.hasRole('maintenance', 'manager', 'admin'));
  const [moving, setMoving] = useState(false);
  const sites = useRows<Site>('sites', 'name');
  const siteOk = useSiteFilter();
  const vehicleSites = (sites ?? []).filter((x) => (x.kind === 'quarry' || x.kind === 'branch') && x.is_active
    && (siteOk(x.id) || x.id === machine?.site_id));

  // The unit readings are in: an existing machine's saved unit (changed only
  // through the meter-change panel), or what a new machine has selected.
  const [meterSaved, setMeterSaved] = useState<Partial<Machine>>({});
  const saved = { ...machine, ...meterSaved } as Machine;
  const savedUnit = unitOf(saved);
  const unit: MeterUnit = isNew ? unitOf(v) : savedUnit;
  const sfx = meterSuffix(unit);

  /** After a meter change, take the fields the server changed. */
  async function refreshMeterFields() {
    const fresh = machine.id
      ? ((await db.table('machines').get(machine.id)) as Record<string, unknown> | undefined)
      : undefined;
    if (fresh) {
      const keys = ['site_id', 'hour_meter_unit', 'current_hour_meter', 'service_interval_hours',
        'last_service_hours', 'last_service_date', 'next_service_hours',
        'meter_replaced_at', 'meter_replaced_old', 'meter_replaced_new'];
      const patch = Object.fromEntries(keys.map((k) => [k, fresh[k]])) as Partial<Machine>;
      setMeterSaved(patch);
      setV((d) => ({ ...d, ...patch }));
    }
    setPendingUnit(null);
  }

  useEffect(() => {
    setV({
      is_active: true,
      status: 'active',
      expected_hours_per_day: 10,
      hour_meter_unit: 'hours',
      opening_hour_meter: 0,
      current_hour_meter: 0,
      ...machine,
    });
  }, [machine]);

  const set = (name: string, value: unknown) => setV((d) => ({ ...d, [name]: value }));

  const type = types.find((t) => t.id === v.machine_type_id);
  const tracksLiners = !!type?.tracks_liners;

  // Convenience: derive the next service from interval + last service.
  const derivedNext =
    v.last_service_hours != null && v.service_interval_hours != null
      ? Number(v.last_service_hours) + Number(v.service_interval_hours)
      : null;

  const svc = machine.id
    ? serviceState({ ...(machine as Machine), ...(v as Machine) }, amberHours, amberKm)
    : null;

  function validate(): boolean {
    const e: Record<string, string> = {};
    if (!v.fleet_no) e.fleet_no = 'Fleet number is required';
    if (!v.machine_type_id) e.machine_type_id = 'Machine type is required';
    if (v.expected_hours_per_day == null) e.expected_hours_per_day = 'Required';
    else if (Number(v.expected_hours_per_day) < 0 || Number(v.expected_hours_per_day) > 24)
      e.expected_hours_per_day = 'Between 0 and 24';
    if (v.current_hour_meter != null && v.opening_hour_meter != null
        && Number(v.current_hour_meter) < Number(v.opening_hour_meter)) {
      e.current_hour_meter = 'Cannot be below the opening reading';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function submit() {
    if (!validate()) return;
    setBusy(true);
    const payload: Draft = { ...v };
    // A meter type change on an existing machine goes through its own panel.
    if (!isNew) payload.hour_meter_unit = savedUnit;
    // So does a move to another site (transfer_machine, 0036).
    if (!isNew) payload.site_id = saved.site_id ?? QUARRY_SITE_ID;
    if (payload.next_service_hours == null && derivedNext != null) {
      payload.next_service_hours = derivedNext;
    }
    if (isNew && payload.current_hour_meter == null) {
      payload.current_hour_meter = Number(payload.opening_hour_meter ?? 0);
    }
    await onSave(payload);
    setBusy(false);
  }

  return (
    <Drawer
      open
      width={720}
      title={isNew ? 'New machine' : `${machine.fleet_no}`}
      subtitle={isNew ? 'Add a machine to the fleet register' : 'Fleet register entry'}
      onClose={onClose}
      footer={tab !== 'details' || locked ? (
        <>
          <span className="spacer" />
          <Button variant="secondary" onClick={onClose}>Close</Button>
        </>
      ) : (
        <>
          {!isNew && (
            <ConfirmRemove
              disabled={busy}
              question={`Remove ${String(machine.fleet_no ?? 'this machine')}? To take it out of service, untick "Active in the fleet" instead.`}
              onConfirm={async () => {
                setBusy(true);
                try {
                  return await onDelete(machine.id!);
                } finally {
                  setBusy(false);
                }
              }}
            />
          )}
          <span className="spacer" />
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} loading={busy}>Save</Button>
        </>
      )}
    >
      {/* Not the Drawer's own lock: the Performance tab stays usable. */}
      {locked && tab === 'details' && <LockNotice>{locked}</LockNotice>}
      {!isNew && (
        <div className="row" style={{ gap: 'var(--sp-2)', marginBottom: 'var(--sp-3)' }}>
          <Button variant={tab === 'details' ? 'primary' : 'secondary'}
            onClick={() => setTab('details')}>Details</Button>
          <Button variant={tab === 'performance' ? 'primary' : 'secondary'}
            onClick={() => setTab('performance')}>Performance</Button>
          {canCorrect && (
            <Button variant={tab === 'readings' ? 'primary' : 'secondary'}
              onClick={() => setTab('readings')}>Readings</Button>
          )}
        </div>
      )}

      {tab === 'performance' && machine.id ? (
        <MachinePerformance machineId={machine.id} />
      ) : tab === 'readings' && machine.id ? (
        <>
          <MeterReplacePanel machine={saved} onDone={refreshMeterFields} />
          <MeterReadings machine={saved} />
        </>
      ) : (
      <fieldset className="fieldset-plain" disabled={Boolean(locked)}>
      {svc && svc.remaining != null && (
        <div className="row" style={{ marginBottom: 'var(--sp-3)' }}>
          <StatusChip tone={svc.rag === 'grey' ? 'grey' : svc.rag}>
            {svc.remaining < 0
              ? `Service overdue by ${unit === 'km' ? num(Math.abs(svc.remaining), 0) : hours(Math.abs(svc.remaining))} ${sfx}`
              : `Service in ${unit === 'km' ? num(svc.remaining, 0) : hours(svc.remaining)} ${sfx}`}
          </StatusChip>
          <span className="small muted">
            next at {num(svc.next, 0)} {sfx} · {meterName(unit)} {num(v.current_hour_meter as number, unit === 'km' ? 0 : 1)} {sfx}
          </span>
        </div>
      )}
      {saved.meter_replaced_at && (
        <p className="small muted" data-testid="meter-replaced-note" style={{ marginTop: 0 }}>
          New {meterName(unit)} fitted {dateTime(saved.meter_replaced_at)}: the old one ended at{' '}
          {num(saved.meter_replaced_old ?? null, unit === 'km' ? 0 : 1)} {sfx}, the new one started at{' '}
          {num(saved.meter_replaced_new ?? null, unit === 'km' ? 0 : 1)} {sfx}. The service count carried over.
        </p>
      )}

      <div className="form-grid">
        <div className="form-section field--full">Identity</div>
        {isNew ? (
          <div className="field--full">
            <SelectInput id="m-site" label="Site" required
              value={(v.site_id as string) ?? QUARRY_SITE_ID}
              options={vehicleSites.map((x) => ({ value: x.id, label: x.name }))}
              onChange={(x) => set('site_id', x ?? QUARRY_SITE_ID)}
              help="Where the vehicle works. Later moves are recorded as transfers." />
          </div>
        ) : (
          <div className="field--full">
            <div className="row" style={{ gap: 'var(--sp-2)', alignItems: 'center' }}>
              <span className="small muted">Site</span>
              <strong data-testid="machine-site">
                {vehicleSites.find((x) => x.id === (saved.site_id ?? QUARRY_SITE_ID))?.name ?? '—'}
              </strong>
              <span className="spacer" />
              {canMove && !moving && (
                <Button variant="secondary" onClick={() => setMoving(true)}>Move to another site</Button>
              )}
            </div>
            <TransferHistory machineId={machine.id!} />
          </div>
        )}
        {moving && !isNew && (
          <TransferPanel machine={saved} onCancel={() => setMoving(false)}
            onDone={async () => { await refreshMeterFields(); setMoving(false); }} />
        )}
        <TextInput id="m-fleet" label="Fleet number" required
          value={String(v.fleet_no ?? '')} error={errors.fleet_no}
          onChange={(x) => set('fleet_no', x)} help="EX-01, ADT-03 — as painted on the machine." />
        <TextInput id="m-name" label="Description" value={String(v.name ?? '')}
          onChange={(x) => set('name', x || null)} />

        <SelectInput id="m-type" label="Machine type" required
          value={(v.machine_type_id as string) ?? null} error={errors.machine_type_id}
          options={types.filter((t) => t.is_active).map((t) => ({ value: t.id, label: t.name }))}
          onChange={(x) => {
            set('machine_type_id', x);
            const t = types.find((y) => y.id === x);
            if (t && !machine.id) set('expected_hours_per_day', t.default_expected_hours);
          }} />
        <SelectInput id="m-status" label="Status" value={(v.status as string) ?? 'active'}
          options={STATUSES} onChange={(x) => set('status', x)}
          help="Breakdowns set this to Down automatically." />

        <TextInput id="m-make" label="Manufacturer" value={String(v.manufacturer ?? '')}
          onChange={(x) => set('manufacturer', x || null)} />
        <TextInput id="m-model" label="Model" value={String(v.model ?? '')}
          onChange={(x) => set('model', x || null)} />

        <TextInput id="m-serial" label="Serial number" value={String(v.serial_no ?? '')}
          onChange={(x) => set('serial_no', x || null)} />
        <TextInput id="m-reg" label="Registration number" value={String(v.registration_no ?? '')}
          onChange={(x) => set('registration_no', x || null)} />

        <div className="form-section field--full">Fuel</div>
        <SelectInput id="m-fuel" label="Usual fuel" value={(v.fuel_type as string) ?? null}
          options={FUEL_TYPES} placeholder="— not set —"
          onChange={(x) => set('fuel_type', x)} help="Optional. A fill of the other fuel is flagged." />
        <NumberInput id="m-fuel-tank" label="Fuel tank" suffix="L" min={0}
          value={(v.fuel_tank_l as number) ?? null} onChange={(x) => set('fuel_tank_l', x)}
          help="Optional. A fill of more than this is flagged." />
        <NumberInput id="m-fuel-norm" label="Expected consumption" suffix={unit === 'km' ? 'L/100 km' : 'L/h'} min={0} step={0.1}
          value={(v.fuel_norm as number) ?? null} onChange={(x) => set('fuel_norm', x)}
          help="Optional. The fuel reports compare actual consumption with it." />

        <div className="form-section field--full">Documents</div>
        <TextInput id="m-vin" label="VIN / chassis number" value={String(v.vin ?? '')}
          onChange={(x) => set('vin', x || null)} />
        <TextInput id="m-insref" label="Insurance policy / reference" value={String(v.insurance_ref ?? '')}
          onChange={(x) => set('insurance_ref', x || null)} />
        {DOCS.map((d) => {
          const state = docState(v[d.key] as string | null | undefined);
          return (
            <TextInput key={d.key} id={`m-${d.key}`} label={`${d.label} expires`} type="date"
              value={String(v[d.key] ?? '')}
              onChange={(x) => set(d.key, x || null)}
              help={state === 'expired' ? 'Expired.' : state === 'due' ? 'Expires within 30 days.' : undefined} />
          );
        })}

        <TextInput id="m-dept" label="Department" value={String(v.department ?? '')}
          onChange={(x) => set('department', x || null)} />
        <TextInput id="m-role" label="Production role" value={String(v.production_role ?? '')}
          onChange={(x) => set('production_role', x || null)}
          help="Loading, Hauling, Crushing…" />

        <div className="form-section field--full">Operating</div>
        <NumberInput id="m-hpd" label="Expected hours per day" required suffix="h"
          min={0} max={24} step={0.5} error={errors.expected_hours_per_day}
          value={(v.expected_hours_per_day as number) ?? null}
          onChange={(x) => set('expected_hours_per_day', x)}
          help="Used when a shift does not set its own scheduled hours." />
        <SelectInput id="m-unit" label="Meter" required
          value={pendingUnit ?? unit}
          options={METER_UNITS}
          onChange={(x) => {
            const next = (x === 'km' ? 'km' : 'hours') as MeterUnit;
            if (isNew) set('hour_meter_unit', next);
            else setPendingUnit(next === savedUnit ? null : next);
          }}
          help={unit === 'km'
            ? 'Trucks and vehicles with an odometer. Readings, services and intervals are in kilometres.'
            : 'Machines with an engine hour meter.'} />
        {pendingUnit && !isNew && (
          <MeterChangePanel machine={saved} to={pendingUnit}
            onCancel={() => setPendingUnit(null)} onDone={refreshMeterFields} />
        )}
        <NumberInput id="m-open" label={`Opening ${meterName(unit)}`} suffix={sfx} min={0} step={unit === 'km' ? 1 : 0.1}
          value={(v.opening_hour_meter as number) ?? null}
          onChange={(x) => set('opening_hour_meter', x)}
          help="The reading when this machine entered the system." />

        <NumberInput id="m-cur" label={`Current ${meterName(unit)}`} suffix={sfx} min={0} step={unit === 'km' ? 1 : 0.1}
          error={errors.current_hour_meter}
          value={(v.current_hour_meter as number) ?? null}
          onChange={(x) => set('current_hour_meter', x)}
          help={isNew ? 'Today’s reading.' : 'Rolls forward from the daily diary — edit only to correct it.'} />
        <NumberInput id="m-payload" label="Payload override" suffix="t" min={0} step={0.5}
          value={(v.default_payload_t as number) ?? null}
          onChange={(x) => set('default_payload_t', x)}
          help="Tonnes per load for this machine. Blank uses the site assumption (30 t)." />

        <div className="form-section field--full">Service</div>
        <NumberInput id="m-int" label="Service interval" suffix={sfx} min={1} step={unit === 'km' ? 1000 : 50}
          value={(v.service_interval_hours as number) ?? null}
          onChange={(x) => set('service_interval_hours', x)} />
        <NumberInput id="m-months" label="Or every" suffix="months" min={1} max={60} step={1}
          value={(v.service_interval_months as number) ?? null}
          onChange={(x) => set('service_interval_months', x)}
          help="Whichever comes first. The service planner uses both." />
        <NumberInput id="m-last" label="Last service at" suffix={sfx} min={0} step={1}
          value={(v.last_service_hours as number) ?? null}
          onChange={(x) => set('last_service_hours', x)} />

        <TextInput id="m-lastdate" label="Last service date" type="date"
          value={String(v.last_service_date ?? '')}
          onChange={(x) => set('last_service_date', x || null)} />
        <NumberInput id="m-next" label="Next service at" suffix={sfx} min={0} step={1}
          value={(v.next_service_hours as number) ?? null}
          onChange={(x) => set('next_service_hours', x)}
          help={derivedNext != null ? `Leave blank to use ${num(derivedNext, 0)} ${sfx}` : undefined} />

        <div className="form-section field--full">Other</div>
        <TextInput id="m-comm" label="Commissioned" type="date"
          value={String(v.commissioned_date ?? '')}
          onChange={(x) => set('commissioned_date', x || null)} />
        <NumberInput id="m-year" label="Year of manufacture" step={1} min={1950} max={2100}
          value={(v.year_of_manufacture as number) ?? null}
          onChange={(x) => set('year_of_manufacture', x)} />

        {(saved.needs_details || v.needs_details) && (
          <div className="field--full">
            <CheckboxInput id="m-needs" label="Needs details"
              value={Boolean(v.needs_details)} onChange={(x) => set('needs_details', x)}
              help="Created from the old fuel log with only what it knew. Untick once the rest is filled in." />
          </div>
        )}

        <div className="field--full">
          <CheckboxInput id="m-active" label="Active in the fleet"
            value={Boolean(v.is_active)} onChange={(x) => set('is_active', x)}
            help="Inactive machines keep their history but are excluded from fleet KPIs and diary capture." />
        </div>

        <div className="field--full">
          <TextArea id="m-notes" label="Notes" value={String(v.notes ?? '')}
            onChange={(x) => set('notes', x || null)} />
        </div>
      </div>

      {!isNew && tracksLiners && <Components machineId={machine.id!} />}
      {!isNew && !tracksLiners && (
        <p className="small muted">
          Wear components are tracked for machine types flagged “tracks liners”.
        </p>
      )}
      </fieldset>
      )}
    </Drawer>
  );
}

/* ------------------------------------------------------------------ */
/* Wear components - the anchor for liner life                         */
/* ------------------------------------------------------------------ */

interface Component {
  id: string;
  machine_id: string;
  code: string;
  name: string;
  position: string | null;
  expected_life_hours: number | null;
  is_active: boolean;
}

function Components({ machineId }: { machineId: string }) {
  const all = useRows<Component>('machine_components', 'code');
  const mine = (all ?? []).filter((c) => c.machine_id === machineId);

  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [life, setLife] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);

  async function add() {
    if (!code || !name) return;
    setBusy(true);
    const ok = await save(
      () => upsert('machine_components', {
        machine_id: machineId, code, name, expected_life_hours: life, is_active: true,
      }),
      'Component added',
    );
    setBusy(false);
    if (ok) { setCode(''); setName(''); setLife(null); }
  }

  return (
    <>
      <div className="form-section">Wear components</div>
      <p className="small muted" style={{ marginTop: 0 }}>
        Each component is where a liner change is recorded, and where average liner
        life is measured from.
      </p>

      {mine.length > 0 && (
        <table className="table" style={{ marginBottom: 'var(--sp-3)' }}>
          <thead>
            <tr><th>Code</th><th>Name</th><th style={{ textAlign: 'right' }}>Expected life</th><th /></tr>
          </thead>
          <tbody>
            {mine.map((c) => (
              <tr key={c.id}>
                <td>{c.code}</td>
                <td>{c.name}</td>
                <td style={{ textAlign: 'right' }} className="tabular">
                  {c.expected_life_hours ? `${num(c.expected_life_hours, 0)} h` : '—'}
                </td>
                <td style={{ textAlign: 'right' }}>
                  <ConfirmRemove compact onConfirm={async () =>
                    (await save(() => softDelete('machine_components', c.id), 'Component removed')) !== null} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="form-grid">
        <TextInput id="c-code" label="Code" value={code} onChange={setCode}
          help="JAW-F, DECK-1…" />
        <TextInput id="c-name" label="Name" value={name} onChange={setName}
          help="Fixed jaw, Top deck…" />
        <NumberInput id="c-life" label="Expected life" suffix="h" min={0} step={50}
          value={life} onChange={setLife} />
        <div style={{ display: 'flex', alignItems: 'flex-start', paddingTop: 22 }}>
          <Button variant="secondary" onClick={add} loading={busy} disabled={!code || !name}>
            Add component
          </Button>
        </div>
      </div>
    </>
  );
}
